// Markdown 渲染工具

/**
 * 完整的 Markdown 渲染器
 * 支持常用的 Markdown 语法
 */
export const renderMarkdown = (content) => {
  if (!content) return '';
  
  let html = content;
  
  // 1. 代码块 (```code```)
  html = html.replace(/```([\s\S]*?)```/g, (match, code) => {
    return `<pre><code>${escapeHtml(code.trim())}</code></pre>`;
  });
  
  // 2. 行内代码 (`code`)
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  
  // 3. 标题 (# ## ###)
  html = html.replace(/^### (.*$)/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gm, '<h1>$1</h1>');
  
  // 4. 粗体 (**text**)
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // 5. 斜体 (*text*)
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // 6. 删除线 (~~text~~)
  html = html.replace(/~~(.*?)~~/g, '<del>$1</del>');
  
  // 7. 链接 [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  
  // 8. 图片 ![alt](url)
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />');
  
  // 9. 引用 (> text)
  html = html.replace(/^> (.*$)/gm, '<blockquote>$1</blockquote>');
  
  // 10. 水平分割线 (--- 或 ***)
  html = html.replace(/^---$/gm, '<hr>');
  html = html.replace(/^\*\*\*$/gm, '<hr>');
  
  // 11. 无序列表 (- 或 *)
  html = html.replace(/^[\-\*] (.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
  
  // 12. 有序列表 (1. 2. 3.)
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
  
  // 13. 表格 (简单实现)
  html = renderTables(html);
  
  // 14. 换行处理
  html = html.replace(/\n\n/g, '</p><p>');
  html = html.replace(/\n/g, '<br>');
  
  // 15. 段落包装
  if (!html.startsWith('<')) {
    html = '<p>' + html + '</p>';
  }
  
  // 16. 清理多余的空段落
  html = html.replace(/<p><\/p>/g, '');
  html = html.replace(/<p><br><\/p>/g, '');
  
  return html;
};

/**
 * 简化版 Markdown 渲染器 (用于预览)
 */
export const renderMarkdownPreview = (content) => {
  if (!content) return '';
  
  let html = content;
  
  // 基础格式化
  html = html.replace(/```([\s\S]*?)```/g, (match, code) => {
    return `<pre><code>${escapeHtml(code.trim())}</code></pre>`;
  });
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  html = html.replace(/^### (.*$)/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gm, '<h1>$1</h1>');
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  html = html.replace(/~~(.*?)~~/g, '<del>$1</del>');
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>');
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />');
  html = html.replace(/^> (.*$)/gm, '<blockquote>$1</blockquote>');
  html = html.replace(/\n/g, '<br>');
  
  return html;
};

/**
 * 表格渲染
 */
const renderTables = (html) => {
  const lines = html.split('\n');
  let inTable = false;
  let tableHtml = '';
  let result = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // 检测表格行
    if (line.includes('|') && line.split('|').length >= 3) {
      if (!inTable) {
        inTable = true;
        tableHtml = '<table><tbody>';
      }
      
      const cells = line.split('|').filter(cell => cell.trim() !== '');
      const isHeaderSeparator = cells.every(cell => /^[\s\-:]+$/.test(cell.trim()));
      
      if (!isHeaderSeparator) {
        tableHtml += '<tr>';
        cells.forEach(cell => {
          tableHtml += `<td>${cell.trim()}</td>`;
        });
        tableHtml += '</tr>';
      }
    } else {
      if (inTable) {
        tableHtml += '</tbody></table>';
        result.push(tableHtml);
        tableHtml = '';
        inTable = false;
      }
      result.push(line);
    }
  }
  
  if (inTable) {
    tableHtml += '</tbody></table>';
    result.push(tableHtml);
  }
  
  return result.join('\n');
};

/**
 * HTML 转义
 */
const escapeHtml = (text) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * 计算字数（支持中英文）
 */
export const countWords = (content) => {
  if (!content) return 0;
  // 移除markdown标记后计算字数
  const plainText = content
    .replace(/```[\s\S]*?```/g, '') // 移除代码块
    .replace(/`[^`]*`/g, '') // 移除行内代码
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // 移除链接，保留文字
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '') // 移除图片
    .replace(/[#*>`\-_~]/g, '') // 移除markdown标记
    .replace(/\s+/g, ''); // 移除空白字符
  return plainText.length;
};

export default {
  renderMarkdown,
  renderMarkdownPreview,
  countWords
};
